import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import i18n from 'i18n-js';
import {
    alpha,
    Box,
    Button,
    Chip,
    IconButton,
    ListItemText,
    makeStyles,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    Toolbar,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';

import { BasicThreeDotsMenu, NothingFoundMessage, PopupConfirmation } from 'components/shared';
import { useAppSelector } from 'index';
import tableMapsStore from 'store/tableMapsStore';
import { theme } from 'config/theme';
import { TableMapListItem } from 'store/tableMapsStore/reducer';
import { Positions, Roles } from 'constants/enums';
import { pagingOptions } from 'constants/arrays';

const useLocalStyles = makeStyles({
    root: {
        minHeight: '100%',
        width: '100%',
        '& .MuiTableCell-body.MuiTableCell-root:first-child': {
            padding: '0 0 0 20px',
        },
        '& th.MuiTableCell-root': {
            padding: 0,
            paddingLeft: 12,
        },
        '& th.MuiTableCell-root:not(:first-child)': {
            height: '10px',
        },
    },
    statusPillActive: {
        backgroundColor: '#e5f8e4',
        color: '#2e7d32',
        fontWeight: 600,
        fontSize: 12,
        borderRadius: 12,
        height: 24,
        '& .MuiChip-label': {
            color: '#2e7d32',
        },
    },
    statusPillInactive: {
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.secondary.dark,
        fontWeight: 600,
        fontSize: 12,
        borderRadius: 12,
        height: 24,
        '& .MuiChip-label': {
            color: theme.palette.secondary.dark,
        },
    },
    actionsCell: {
        '& .MuiIconButton-root': {
            padding: 3,
        },
    },
    tableCellHeader: {
        fontSize: '14px',
        fontWeight: '400' as any,
        color: alpha(theme.palette.common.black, 0.6),
    },
    tableCellHeaderContainer: {
        marginBottom: 0,
        marginTop: 3,
    },
    iconButton: {
        color: theme.palette.common.black,
        marginRight: '10px',
    },
    nameLink: {
        cursor: 'pointer',
        color: theme.palette.primary.main,
        fontWeight: 600,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    updatedCell: {
        fontSize: 13,
        color: theme.palette.text.primary,
        whiteSpace: 'nowrap',
    },
    threeDotsMenuItemContainer: {
        display: 'flex',
        padding: '2em',
        margin: '-2em',
        alignItems: 'center',
        width: '100%',
    },
    threeDotsItemIcon: {
        fill: alpha(theme.palette.common.black, 0.54),
        paddingRight: '20px',
    },
});

function formatUpdatedAt(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    const date = d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
    const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `${date}, ${time}`;
}

export default function TableMapsTable() {
    const classes = useLocalStyles();
    const dispatch = useDispatch();
    const history = useHistory();

    const list = useAppSelector((state) => state.tableMaps.list);
    const user = useAppSelector((state) => state.user);
    const currentRestaurant = user.restaurants?.find((r) => r.id === user.currentRestaurantId);

    const isManagerOrOwner =
        user.role === Roles.OWNER || currentRestaurant?.currentPosition === Positions.MANAGER;

    const [deleteTarget, setDeleteTarget] = useState<TableMapListItem | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(pagingOptions[0]);

    // Clamp page index when the list shrinks (e.g. after deletion)
    useEffect(() => {
        const lastPage = Math.max(0, Math.ceil(list.length / rowsPerPage) - 1);
        if (page > lastPage) setPage(lastPage);
    }, [list.length, rowsPerPage, page]);

    useEffect(() => {
        dispatch(tableMapsStore.actionCreators.getTableMaps());
    }, []);

    function handleCreate() {
        history.push('/table-maps/create');
    }

    function handleView(id: string) {
        history.push(`/table-maps/${id}`);
    }

    function handleEdit(id: string) {
        history.push(`/table-maps/${id}/edit`);
    }

    function handleDeleteConfirm() {
        if (deleteTarget) {
            dispatch(tableMapsStore.actionCreators.deleteTableMap(deleteTarget.id));
        }
        setDeleteTarget(null);
    }

    function handleChangePage(_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) {
        setPage(newPage);
    }

    function handleChangeRowsPerPage(e: React.ChangeEvent<HTMLInputElement>) {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    }

    function buildMenuItems(row: TableMapListItem): JSX.Element[] {
        return [
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleView(row.id)}
                key="view"
            >
                <VisibilityIcon className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('tableMaps.actionViewDetails')} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => handleEdit(row.id)}
                key="edit"
            >
                <EditIcon className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('tableMaps.actionEdit')} />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() =>
                    dispatch(
                        tableMapsStore.actionCreators.setTableMapActive(
                            row.id,
                            row.status !== 'Active'
                        ) as never
                    )
                }
                key="toggle-active"
            >
                {row.status === 'Active' ? (
                    <BlockIcon className={classes.threeDotsItemIcon} />
                ) : (
                    <CheckCircleOutlineIcon className={classes.threeDotsItemIcon} />
                )}
                <ListItemText
                    primary={
                        row.status === 'Active'
                            ? i18n.t('tableMaps.actionDisable')
                            : i18n.t('tableMaps.actionEnable')
                    }
                />
            </div>,
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => setDeleteTarget(row)}
                key="delete"
            >
                <DeleteIcon className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('tableMaps.actionDelete')} />
            </div>,
        ];
    }

    const pagedList = list.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <>
            <Toolbar style={{ justifyContent: 'space-between', paddingBottom: '10px' }}>
                <Box display="flex" alignItems="center">
                    <IconButton size="small" color="primary" onClick={() => history.push('/')}>
                        <ArrowBackIcon fontSize="inherit" />
                    </IconButton>
                    <Tabs value={0} indicatorColor="primary" textColor="primary">
                        <Tab value={0} label={i18n.t('tableMaps.title')} />
                    </Tabs>
                </Box>
                {isManagerOrOwner && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                    >
                        {i18n.t('tableMaps.createButton').toUpperCase()}
                    </Button>
                )}
            </Toolbar>

            <Paper
                style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
                <Box style={{ flex: 1, overflow: 'hidden', paddingTop: 16 }}>
                    <TableContainer style={{ maxHeight: '100%' }}>
                        <Table stickyHeader className={classes.root} aria-label="table maps table">
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        className={classes.tableCellHeader}
                                        style={{ paddingLeft: 18 }}
                                        width={'24%'}
                                    >
                                        <Box className={classes.tableCellHeaderContainer}>
                                            {i18n.t('tableMaps.columnName')}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellHeader} width={'12%'}>
                                        <Box className={classes.tableCellHeaderContainer}>
                                            {i18n.t('tableMaps.columnTables')}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellHeader} width={'14%'}>
                                        <Box className={classes.tableCellHeaderContainer}>
                                            {i18n.t('tableMaps.columnTotalSeats')}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellHeader} width={'14%'}>
                                        <Box className={classes.tableCellHeaderContainer}>
                                            {i18n.t('tableMaps.columnStatus')}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellHeader} width={'22%'}>
                                        <Box className={classes.tableCellHeaderContainer}>
                                            {i18n.t('tableMaps.columnUpdated')}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellHeader}>
                                        <div
                                            style={{ padding: 16, width: 100 }}
                                            className={classes.tableCellHeaderContainer}
                                        >
                                            {i18n.t('tableMaps.columnActions')}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {list.length > 0 ? (
                                    pagedList.map((row) => {
                                        const updatedFormatted = formatUpdatedAt(row.updatedAt);
                                        const isActive = row.status === 'Active';
                                        return (
                                            <TableRow key={row.id}>
                                                <TableCell>
                                                    <span
                                                        className={classes.nameLink}
                                                        onClick={() => handleView(row.id)}
                                                        role="button"
                                                        tabIndex={0}
                                                        onKeyDown={(e) =>
                                                            e.key === 'Enter' && handleView(row.id)
                                                        }
                                                    >
                                                        {row.name}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <Box component="span">{row.tablesCount}</Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box component="span">{row.totalSeats}</Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={
                                                            isActive
                                                                ? i18n.t('tableMaps.statusActive')
                                                                : i18n.t('tableMaps.statusInactive')
                                                        }
                                                        className={
                                                            isActive
                                                                ? classes.statusPillActive
                                                                : classes.statusPillInactive
                                                        }
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <span className={classes.updatedCell}>
                                                        {updatedFormatted ?? '—'}
                                                    </span>
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    className={classes.actionsCell}
                                                >
                                                    {isManagerOrOwner && (
                                                        <Box ml="10px">
                                                            <BasicThreeDotsMenu
                                                                items={buildMenuItems(row)}
                                                            />
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={6} style={{ height: 400 }}>
                                            <NothingFoundMessage />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <TablePagination
                    rowsPerPageOptions={pagingOptions}
                    component="div"
                    count={list.length}
                    rowsPerPage={rowsPerPage}
                    labelRowsPerPage={i18n.t('common.rowsPerPage')}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <PopupConfirmation
                open={deleteTarget !== null}
                close={() => setDeleteTarget(null)}
                title={i18n.t('tableMaps.deleteTitle')}
                description={i18n.t('tableMaps.deleteDescription')}
                activeBtn={i18n.t('tableMaps.actionDelete')}
                action={handleDeleteConfirm}
            />
        </>
    );
}
